<template>
  <div v-if="event && isSupportedType">
    <h1 class="mt-4" data-testid="invite-form-title">{{ t('EventForm.editEvent') }}</h1>

    <BackLink class="mt-4" :to="backLinkTarget">{{ t('backToOverview') }}</BackLink>

    <div v-if="eventLiveUrl" class="mt-2 card py-6">
      <p class="mb-0 pb-0">
        {{ t('EventForm.digitalMasterclassView.watchEventText') }}
        <a :href="eventLiveUrl" target="_blank">{{ t('EventForm.digitalMasterclassView.watchEventCta') }}</a>
      </p>
    </div>

    <ContentCard v-if="!loading" class="mt-2">
      <template #header>
        <EventItem
          v-if="event.id && event.type && isSupportedType"
          :event="event"
          :show-main-button="false"
          :show-chip="true"
          :show-dropdown="false"
        />
      </template>

      <div class="my-6">
        <TextInput
          v-if="config.value.editStreamingKey"
          v-model="formData.streamingKey"
          :label="t('EventForm.streamingKey').toString()"
          data-testid="invite-stream-key"
        />
        <TextInput
          v-model="formData.nameDe"
          :label="t('titleDe') + '*'"
          :error="getValidationErrors(v$.nameDe)"
          :max-length="MAX_TITLE_LENGTH"
          data-testid="invite-form-name-de"
          @update:model-value="v$.nameDe.$validate"
        />
        <TextInput
          v-model="formData.nameEn"
          :label="t('titleEn') + '*'"
          :error="getValidationErrors(v$.nameEn)"
          :max-length="MAX_TITLE_LENGTH"
          data-testid="invite-form-name-en"
          @update:model-value="v$.nameEn.$validate"
        />
        <TextInput
          v-model="formData.descriptionDe"
          type="textarea"
          :label="t('EventForm.descriptionDe') + '*'"
          :max-length="750"
          :error="getValidationErrors(v$.descriptionDe)"
          data-testid="invite-form-desc-de"
          @update:model-value="v$.descriptionDe.$validate"
          @blur="v$.descriptionDe.$validate"
        />
        <TextInput
          v-model="formData.descriptionEn"
          type="textarea"
          :label="t('EventForm.descriptionEn') + '*'"
          :max-length="750"
          :error="getValidationErrors(v$.descriptionEn)"
          data-testid="invite-form-desc-en"
          @update:model-value="v$.descriptionEn.$validate"
          @blur="v$.descriptionEn.$validate"
        />
        <div>
          <SelectInput
            v-model="formData.language"
            :error="getValidationErrors(v$.language)"
            :label="t('EventForm.language') + '*'"
            data-testid="invite-form-language"
            @change="v$.language.$validate"
          >
            <option v-for="option in languageOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
          </SelectInput>
        </div>
        <div v-if="config.value.editVideo">
          <p class="mt-8">{{ t('EventForm.videoUpload.intro') }}</p>
          <fieldset :disabled="uploadBusy || saving" class="mt-4 flex flex-col gap-4 tablet:flex-row">
            <RadioCard
              v-model="uploadMode"
              :disabled="uploadBusy || saving"
              value="combined"
              name="upload-mode"
              data-testid="invite-upload-mode-combined"
            >
              <span class="block">{{ t('EventForm.videoUpload.optionCombined') }}</span>
              <span class="mt-1 block text-subtle">{{ t('EventForm.videoUpload.optionCombinedHint') }}</span>
              <span class="relative mt-3 block aspect-video w-56 max-w-full rounded-sm border-2 border-main bg-surface-elevation-1">
                <span class="absolute top-1.5 right-1.5 block h-1/4 w-1/4 rounded-xs border-2 border-main bg-surface"></span>
              </span>
            </RadioCard>
            <RadioCard
              v-model="uploadMode"
              :disabled="uploadBusy || saving"
              value="separate"
              name="upload-mode"
              data-testid="invite-upload-mode-separate"
            >
              <span class="block">{{ t('EventForm.videoUpload.optionSeparate') }}</span>
              <span class="mt-1 block text-subtle">{{ t('EventForm.videoUpload.optionSeparateHint') }}</span>
              <span class="mt-3 block w-56 max-w-full">
                <span class="block aspect-video w-full rounded-sm border-2 border-main bg-surface-elevation-1"></span>
                <span class="mt-1.5 ml-auto block aspect-video w-1/4 rounded-xs border-2 border-main bg-surface-elevation-1"></span>
              </span>
            </RadioCard>
          </fieldset>
          <div class="mt-8">
            <span v-if="!event.videoRecordingUrl || reuploadVideoRecording">
              <FileUploadAsync
                v-model="formData.videoRecording"
                :disabled="saving"
                data-testid="invite-recording-upload"
                :icon="mdiPaperclip"
                :label="videoRecordingLabel"
                :hint="videoRecordingHint"
                accept="video/mp4,video/*,.zip"
                class="mt-4 mb-2"
                @state-change="recordingState = $event"
              />
            </span>
            <AlertBox v-else type="success" :icon-path="mdiPaperclip">
              <div class="flex flex-row items-center justify-between">
                <span>
                  {{ videoRecordingLabel }}:
                  <a v-if="event.videoRecordingUrl" :href="event.videoRecordingUrl">
                    {{ videoRecordingName }}
                  </a>
                </span>
                <button
                  class="btn-secondary-color-s ml-12"
                  :disabled="saving"
                  data-testid="invite-reupload-rec"
                  @click="reuploadVideoRecording = true"
                >
                  {{ t('EventForm.reuploadAction') }}
                </button>
              </div>
            </AlertBox>
          </div>

          <div v-if="uploadMode === 'separate'" class="mt-8">
            <span v-if="!event.videoPresentationUrl || reuploadVideoPresentation">
              <FileUploadAsync
                v-model="formData.videoPresentation"
                :disabled="saving"
                data-testid="invite-presentation-upload"
                :icon="mdiPaperclip"
                :label="t('EventForm.videoUpload.presentationLabel')"
                :hint="t('EventForm.videoUpload.presentationHint')"
                accept="video/mp4,video/*"
                class="mt-4 mb-2"
                @state-change="presentationState = $event"
              />
            </span>
            <AlertBox v-else type="success" :icon-path="mdiPaperclip">
              <div class="flex flex-row items-center justify-between">
                <span>
                  {{ t('EventForm.videoUpload.presentationLabel') }}:
                  <a v-if="event.videoPresentationUrl" :href="event.videoPresentationUrl">
                    {{ videoPresentationName }}
                  </a>
                </span>
                <button
                  class="btn-secondary-color-s ml-12"
                  :disabled="saving"
                  data-testid="invite-reupload-pres"
                  @click="reuploadVideoPresentation = true"
                >
                  {{ t('EventForm.reuploadAction') }}
                </button>
              </div>
            </AlertBox>
          </div>
          <TimelineClicker
            v-if="event.videoRecordingUrl"
            :src="event.videoRecordingUrl"
            :agenda-timestamps="formData.agendaFormatted || '[]'"
            @update-agenda="updateAgenda"
          />
        </div>

        <div class="mt-10">
          <h2 class="pb-3">{{ t('EventForm.interests') }} ({{ formData.interests.length }}/4)</h2>

          <AlertBox v-if="showSelectableInterestLimitAlert" type="warning" class="mt-2">
            <template #header>
              {{ t('EventForm.interestsLimitWarning') }}
            </template>
          </AlertBox>

          <div class="chips-list">
            <SelectableInterest
              v-for="interest in availableInterests"
              :key="interest.id"
              :interest="interest"
              :selected="selected(interest)"
              @toggle-selection="toggleSelection"
            />
          </div>
        </div>
      </div>

      <MessageBanner v-if="saveErrors.length" type="error" role="alert" data-testid="invite-form-errors">
        <p v-for="message in saveErrors" :key="message">{{ message }}</p>
      </MessageBanner>

      <template #footer>
        <div class="flex justify-end p-4 tablet:text-right">
          <RouterLink :to="{ name: 'events' }" class="btn-secondary-color-s mr-4" data-testid="invite-form-cancel">
            {{ t('cancel') }}
          </RouterLink>

          <button
            class="btn-primary-color-s"
            :disabled="v$.$invalid || uploadBlocked || saving"
            data-testid="invite-form-save"
            @click.stop="updateEvent"
          >
            <span v-if="!saving">{{ t('saveButtonLabel') }}</span>
            <span v-else>{{ t('EventForm.processing') }}</span>
          </button>
        </div>
      </template>
    </ContentCard>
  </div>
</template>

<script setup lang="ts">
import { mdiPaperclip } from '@mdi/js'
import { MessageBanner, RadioCard } from '@ramp106/omrjs-core-ui'
import { SelectInput, TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import * as Sentry from '@sentry/vue'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import {
  EventTypeEnum,
  type UpdateEventMutationVariables,
  useEventQuery,
  type InterestsQuery,
  useInterestsQuery,
  useUpdateEventMutation,
} from '@/gql/myomr'
import { toGlobalId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, maxLength } from '@/services/validators'
import type { DirectUploadFile, DirectUploadState } from '@/types'
import BackLink from '@/ui/BackLink.vue'

import useEventFormConfig from './eventFormConfig'
import { default as EventItem } from './EventItem.vue'

const route = useRoute()
const i18n = useI18n()
const t = i18n.t

type Interest = InterestsQuery['interests']['nodes'][number]

const config = computed(() => {
  return useEventFormConfig(event.value?.type)
})

const MAX_TITLE_LENGTH = 90
const MAX_DESCRIPTION_LENGTH = 750
const NOTIFICATION_MILLISECONDS = 5000
type FormDataType = Omit<
  Required<UpdateEventMutationVariables>,
  'eventId' | 'interests' | 'videoPresentationBlobId' | 'videoRecordingBlobId'
> & {
  interests: string[]
  videoPresentation: DirectUploadFile | undefined
  videoRecording: DirectUploadFile | undefined
}
const formData = reactive<FormDataType>({
  agendaFormatted: '',
  companyEmail: '',
  companyName: '',
  companyWebsite: '',
  ctaButtonLink: '',
  ctaButtonText: '',
  descriptionDe: '',
  descriptionEn: '',
  duration: '',
  endsAt: '',
  eventFormat: '',
  interests: [],
  language: 'de',
  locationAddress: '',
  locationName: '',
  nameDe: '',
  nameEn: '',
  shuttleService: false,
  startsAt: '',
  streamingKey: '',
  videoPresentation: undefined,
  videoRecording: undefined,
})

const languageOptions = [
  { value: 'de', text: t('de') },
  { value: 'en', text: t('en') },
] as const

const {
  result: eventResult,
  loading,
  refetch: refetchEvent,
  onResult: onEventResult,
} = useEventQuery({ id: toGlobalId('Event', route.params.eventId as string) })
const event = computed(() => eventResult.value?.eventById)
const isSupportedType = computed(() => (event.value?.type || '') in EventTypeEnum)

const rules = {
  descriptionDe: { required, maxLength: maxLength(MAX_DESCRIPTION_LENGTH) },
  descriptionEn: { required, maxLength: maxLength(MAX_DESCRIPTION_LENGTH) },
  language: { required },
  nameDe: { required, maxLength: maxLength(MAX_TITLE_LENGTH) },
  nameEn: { required, maxLength: maxLength(MAX_TITLE_LENGTH) },
}
const v$ = useVuelidate(rules, formData)

onEventResult((result) => {
  if (result?.data?.eventById) {
    const { interests, ...newData } = result.data.eventById
    Object.assign(formData, newData)
    formData.interests = interests.map((i) => i.id)
    if (!uploadModeInitialized) {
      uploadMode.value = result.data.eventById.videoPresentationUrl ? 'separate' : 'combined'
      uploadModeInitialized = true
    }
  }
})

const uploadMode = ref<'separate' | 'combined'>('combined')
let uploadModeInitialized = false
const recordingState = ref<DirectUploadState>('idle')
const presentationState = ref<DirectUploadState>('idle')
const uploadBusy = computed(() =>
  [recordingState.value, presentationState.value].some((state) => state === 'preparing' || state === 'uploading'),
)
const uploadBlocked = computed(() => uploadBusy.value || recordingState.value === 'error' || presentationState.value === 'error')

watch(uploadMode, (mode) => {
  if (mode === 'combined') {
    formData.videoPresentation = undefined
    presentationState.value = 'idle'
  }
})

const videoRecordingLabel = computed(() =>
  t(uploadMode.value === 'combined' ? 'EventForm.videoUpload.combinedLabel' : 'EventForm.videoUpload.speakerLabel'),
)
const videoRecordingHint = computed(() =>
  t(uploadMode.value === 'combined' ? 'EventForm.videoUpload.combinedHint' : 'EventForm.videoUpload.speakerHint'),
)

const videoPresentationUrlLink = computed(() => event.value?.videoPresentationUrl?.split('/').slice(-1)[0] || '')
const reuploadVideoPresentation = ref(false)

const videoRecordingUrlLink = computed(() => event.value?.videoRecordingUrl?.split('/').slice(-1)[0] || '')
const reuploadVideoRecording = ref(false)

function updateAgenda(agenda: string) {
  formData.agendaFormatted = agenda
}

const MAX_INTERESTS = 4
const showSelectableInterestLimitAlert = ref(false)
const { result: interestsResult } = useInterestsQuery()
const availableInterests = computed(() => interestsResult.value?.interests.nodes || [])
const selected = (interest: Interest) => formData.interests.includes(interest.id)
function toggleSelection({ interest, selected }: { interest: Interest; selected: boolean }) {
  if (selected) {
    addInterest(interest)
  } else {
    removeInterest(interest)
  }
}
function addInterest(interest: Interest) {
  if (formData.interests.length < MAX_INTERESTS) {
    formData.interests.push(interest.id)
  } else {
    showSelectableInterestLimitAlert.value = true
    setTimeout(() => (showSelectableInterestLimitAlert.value = false), NOTIFICATION_MILLISECONDS)
  }
}
function removeInterest(interest: Interest) {
  const index = formData.interests.indexOf(interest.id)
  if (index > -1) formData.interests.splice(index, 1)
}

const eventLiveUrl = computed(() => {
  if (event.value?.type != 'DigitalMasterclass') return null

  return event.value?.livestreamUrlWithToken
})

const { mutate } = useUpdateEventMutation({})
const saving = ref(false)
const saveErrors = ref<string[]>([])

async function scrollToFirstError() {
  const errorInputs = document.getElementsByClassName('form-input-error')
  if (errorInputs.length) {
    errorInputs[0].scrollIntoView({ behavior: 'smooth' })
  }
}

async function updateEvent() {
  if (!event.value || saving.value || uploadBlocked.value) return
  saving.value = true
  saveErrors.value = []
  try {
    const isFormValid = await v$.value.$validate()
    if (!isFormValid) {
      await scrollToFirstError()
      return
    }
    if (uploadBlocked.value) return
    const { videoPresentation, videoRecording, ...attributes } = formData
    const response = await mutate({
      ...attributes,
      eventId: event.value.id,
      videoPresentationBlobId: videoPresentation?.blobId,
      videoRecordingBlobId: videoRecording?.blobId,
    })
    const result = response?.data?.updateEvent
    if (!result || !Array.isArray(result.errors)) throw new Error('Missing event update response')
    if (result.errors.length) {
      saveErrors.value = result.errors
      return
    }

    formData.videoPresentation = undefined
    formData.videoRecording = undefined
    recordingState.value = 'idle'
    presentationState.value = 'idle'
    reuploadVideoPresentation.value = false
    reuploadVideoRecording.value = false
    showNotification(t('EventForm.successfulUpdateNotification'), 'success')
    try {
      const refreshed = await refetchEvent()
      if (refreshed?.error || refreshed?.errors?.length || !refreshed?.data?.eventById) {
        throw new Error('Event refresh failed')
      }
    } catch (error) {
      showNotification(t('EventForm.savedRefreshFailed'), 'warning')
      Sentry.captureException(error)
    }
  } catch (error) {
    saveErrors.value = [t('EventForm.failedUpdateNotification')]
    Sentry.captureException(error)
  } finally {
    saving.value = false
  }
}

const backLinkTarget = computed(() => {
  const endsAtString = event.value?.endsAt
  if (endsAtString) {
    const endsAt = new Date(endsAtString)
    if (endsAt < new Date()) {
      return { name: 'events', params: { selector: 'archive' } }
    }
  }
  return { name: 'events' }
})

function extractFilenameFromUrl(rawUrl: string): string | null {
  try {
    const query = rawUrl.split('?')[1]
    if (!query) return null

    const decoded = decodeURIComponent(query)

    const filenameStarMatch = decoded.match(/filename\*\s*=\s*UTF-8''([^;\n]+)/)
    const filenameMatch = decoded.match(/filename\s*=\s*"([^"]+)"/)

    const filename = filenameStarMatch?.[1] || filenameMatch?.[1]

    if (!filename) return null

    const clean = filename.match(/[^&]+?\.(mp4|mov|webm|avi|mkv)/i)
    return clean ? clean[0] : filename
  } catch {
    return null
  }
}

const videoPresentationName = computed(() => {
  return extractFilenameFromUrl(videoPresentationUrlLink.value)
})

const videoRecordingName = computed(() => {
  return extractFilenameFromUrl(videoRecordingUrlLink.value)
})
</script>
